"use client";

import { useEffect, useState } from "react";
import { SiteNav, SiteFooter, HIGH_VALUE_STYLES } from "./chrome";
import { STYLES as BASE_STYLES } from "./LandingV2";

const DISCOVERY_ANCHOR = "#discovery";
const BOOKING_IFRAME_SRC = "https://get.lvlup-app.com/widget/booking/TRinz5WYe6l9l5UvGF11";

function Check({ children }: { children: React.ReactNode }) {
  return (
    <li>
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l3 3 7-7" /></svg>
      {children}
    </li>
  );
}

const modules = [
  {
    title: "Build & Sell Your Own Courses",
    desc: "Turn your knowledge into a productized offer. Author multi-module courses with lessons and assessments, then sell them on your own branded app — no commission taken.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h14v11H3zM3 4l7-3 7 3" /></svg>
    ),
    bullets: ["Multi-module course builder", "Interactive lessons & quizzes", "Drip-feed content delivery", "Sell once, deliver to thousands"],
  },
  {
    title: "Sell Ready-Made Programs",
    desc: "Productize your coaching for the audience you can't reach 1:1. Package workouts, nutrition, and follow-along plans as ready-made programs and sell them at scale on your branded app.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 2L4 11h5l-1 7 7-9h-5l1-7z" /></svg>
    ),
    bullets: ["Build home & gym workout programs", "Nutrition plan templates", "Beginner to advanced levels", "Zero commission — keep 100%"],
  },
  {
    title: "Public & Private Communities",
    desc: "Create unlimited communities — public to grow your brand, private to give paying clients a high-value space. Posts, replies, and reactions all on your branded app.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="7" cy="7" r="3" /><circle cx="14" cy="9" r="2.5" /><path d="M2 16a5 5 0 0110 0M12 16a4 4 0 016 0" /></svg>
    ),
    bullets: ["Unlimited public & private spaces", "Posts, threads, reactions", "Members-only paid communities", "Moderation tools built in"],
  },
  {
    title: "Follow-Along Video Library",
    desc: "Ship pro-quality follow-along workouts that clients press play on. Perfect for at-home programs, mobility flows, and recorded coaching sessions.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="14" height="10" rx="2" /><path d="M8 8l5 2-5 2V8z" fill="currentColor" /></svg>
    ),
    bullets: ["HD workout videos", "Multiple fitness categories", "Auto-synced to client programs", "Drop new content any time"],
  },
  {
    title: "Engagement Challenges",
    desc: "Create exciting challenges that boost client motivation and retention. Gamify the fitness journey with leaderboards and rewards.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 1l2 4 4 .5-3 3 1 4-4-2-4 2 1-4-3-3 4-.5z" /></svg>
    ),
    bullets: ["Custom challenge builder", "Leaderboards & rankings", "Achievement badges", "Social sharing features"],
  },
  {
    title: "Advanced App Customization",
    desc: "Go beyond basic branding with advanced customization options. Create a truly unique app experience that reflects your brand identity.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="10" r="3" /><circle cx="14" cy="10" r="3" /><path d="M6 5V3M14 5V3M6 17v-2M14 17v-2" /></svg>
    ),
    bullets: ["Custom UI/UX design", "Advanced color schemes", "Custom app flows", "Branded loading screens"],
  },
  {
    title: "Multiple Coach Management",
    desc: "Scale your business with team management features. Add multiple coaches with role-based permissions and client assignments.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2.5" /><path d="M2 13a4 4 0 018 0M11 5a2 2 0 110 4M15 13a3 3 0 00-3-3" /></svg>
    ),
    bullets: ["Team coach accounts", "Role-based permissions", "Client assignment system", "Performance analytics per coach"],
  },
];

const brandedApps = [
  {
    overline: "ONE-ON-ONE PERSONAL TRAINING",
    title: "Powerful for Life",
    blurb: "Complete physique transformation app with custom branding for 1-on-1 personal training.",
    tags: ["PERSONALIZED PLANS", "NUTRITION TRACKING", "WEEKLY CHECK-INS"],
    image: "/images/powerful-for-life.webp",
    hoverImage: "/images/sg-coaching.jpeg",
  },
  {
    overline: "HYROX, RUNNING & STRENGTH",
    title: "ReadyFitness",
    blurb: "Ready-made Hyrox, running, and personalized strength programs for performance athletes.",
    tags: ["READY-MADE HYROX", "RUNNING PROGRAMS", "STRENGTH PLANS"],
    image: "/images/ready.webp",
    hoverImage: "/images/ready-app.jpeg",
  },
  {
    overline: "HANDSTAND & MINDFUL COACHING",
    title: "Mindful Somatics",
    blurb: "Professional handstand skill and strength training app for serious athletes.",
    tags: ["HANDSTAND TECHNIQUE", "GUIDED MEDITATION", "STRENGTH TRAINING"],
    image: "/images/chala.webp",
    hoverImage: "/images/mindful-somatics.jpeg",
  },
  {
    overline: "HANDSTANDS & MOBILITY",
    title: "Go Upsidedown",
    blurb: "Comprehensive handstand, calisthenics, and mobility training app.",
    tags: ["HANDSTANDS", "CALISTHENICS", "MOBILITY"],
    image: "/images/caro.webp",
    hoverImage: "/images/go-upsidedown.jpeg",
  },
];

const languages = [
  { flag: "🇬🇧", name: "English", region: "GLOBAL MARKET" },
  { flag: "🇫🇷", name: "French", region: "FRANCE · CANADA" },
  { flag: "🇸🇦", name: "Arabic", region: "MIDDLE EAST · N. AFRICA" },
  { flag: "🇪🇸", name: "Spanish", region: "SPAIN · LATIN AMERICA" },
  { flag: "🇩🇪", name: "German", region: "GERMANY · AUSTRIA" },
  { flag: "🇷🇺", name: "Russian", region: "RUSSIA · E. EUROPE" },
  { flag: "🇮🇹", name: "Italian", region: "ITALY · SWITZERLAND" },
  { flag: "+", name: "More languages", region: "ADDITIONAL ON REQUEST", isPlus: true },
];

export default function HighValueV2() {
  const [hovered, setHovered] = useState<number | null>(null);
  const [calendarReady, setCalendarReady] = useState(false);
  useEffect(() => setCalendarReady(true), []);

  return (
    <div className="lvlup-landing">
      <style dangerouslySetInnerHTML={{ __html: BASE_STYLES + HIGH_VALUE_STYLES }} />
      <SiteNav current="high-value" />

      <main>

      {/* HERO */}
      <section className="hv-hero">
        <div className="hv-hero-grid" />
        <div className="hv-hero-glow" />
        <div className="lp-container hv-hero-inner">
          <span className="lp-eyebrow">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor"><path d="M6 1l1.6 3.3L11 4.9l-2.5 2.4.6 3.5L6 9.2 2.9 10.8l.6-3.5L1 4.9l3.4-.6L6 1z" /></svg>
            Elite Coaching Solution
          </span>
          <h1>The ultimate platform for <span className="lp-accent">elite coaches</span></h1>
          <p className="hv-lead">
            Go beyond basic coaching tools. Access advanced modules, premium features, and unlimited
            customization to create extraordinary client experiences that command premium prices.
          </p>
          <div className="hv-hero-ctas">
            <a href="#features" className="lp-btn lp-btn-primary lp-btn-lg">
              Explore Premium Features
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 7h8m-3-3l3 3-3 3" /></svg>
            </a>
            <a href={DISCOVERY_ANCHOR} className="lp-btn lp-btn-ghost lp-btn-lg">Book Discovery Call</a>
          </div>

          <div className="hv-quad">
            <div className="hv-quad-cell">
              <div className="hv-quad-icon"><svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h11v9H3zM3 4l5-2 6 2" /></svg></div>
              <h3>Course Creation</h3>
              <p>Build &amp; sell courses with structured modules.</p>
            </div>
            <div className="hv-quad-cell">
              <div className="hv-quad-icon"><svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 2L4 11h5l-1 7 7-9h-5l1-7z" /></svg></div>
              <h3>Ready-Made Programs</h3>
              <p>Productize your coaching and sell at scale.</p>
            </div>
            <div className="hv-quad-cell">
              <div className="hv-quad-icon"><svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2.5" /><circle cx="13" cy="8" r="2" /><path d="M2 14a4 4 0 018 0M11 14a3 3 0 016 0" /></svg></div>
              <h3>Communities</h3>
              <p>Public &amp; private spaces to drive engagement.</p>
            </div>
            <div className="hv-quad-cell">
              <div className="hv-quad-icon"><svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h12v12H3z" /><path d="M3 7h12M7 3v12" /></svg></div>
              <h3>Advanced Branding</h3>
              <p>Complete customization — icons, fonts, screens.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 01 ADVANCED MODULES */}
      <section id="features" className="hv-block">
        <div className="lp-container">
          <div className="hv-block-head">
            <span className="lp-eyebrow">01 · ADVANCED MODULES</span>
            <h2>Advanced modules &amp; <span className="lp-accent">features</span></h2>
            <p>Everything you need to deliver premium coaching experiences and scale your business to new heights.</p>
          </div>
          <div className="hv-modules">
            {modules.map((m) => (
              <div key={m.title} className="hv-module">
                <div className="hv-module-icon">{m.icon}</div>
                <h3>{m.title}</h3>
                <p>{m.desc}</p>
                <ul>
                  {m.bullets.map((b) => (
                    <Check key={b}>{b}</Check>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 02 BRANDED APPS */}
      <section className="hv-block">
        <div className="lp-container">
          <div className="hv-block-head">
            <span className="lp-eyebrow">02 · BRANDED APPS</span>
            <h2>Fully <span className="lp-accent">branded app</span> experiences</h2>
            <p>We don't just change an icon — we create completely unique, fully branded experiences for each coach. See how we've transformed coaching businesses with custom app designs.</p>
          </div>
          <div className="hv-branded">
            {brandedApps.map((b, i) => (
              <div
                key={b.title}
                className="hv-brand"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="hv-brand-poster hv-brand-poster-img">
                  <img
                    src={b.image}
                    alt={`${b.title} branded coaching app — ${b.overline.toLowerCase()}`}
                    className={`hv-brand-img hv-brand-img-base ${hovered === i ? "hv-brand-img-out" : ""}`}
                    loading="lazy"
                  />
                  <img
                    src={b.hoverImage}
                    alt={`${b.title} branded coaching app in-app screen`}
                    className={`hv-brand-img hv-brand-img-hover ${hovered === i ? "hv-brand-img-in" : ""}`}
                    loading="lazy"
                  />
                  <div className="hv-brand-overlay">
                    <span>{b.overline}</span>
                    <h4>{b.title}</h4>
                  </div>
                </div>
                <div className="hv-brand-body">
                  <p>{b.blurb}</p>
                  <div className="hv-brand-tags">
                    {b.tags.map((t) => (<span key={t}>{t}</span>))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <p style={{ fontSize: 14, color: "var(--ink-dim)", margin: "0 0 16px" }}>Ready to see your brand come to life in a custom app?</p>
            <a href={DISCOVERY_ANCHOR} className="lp-btn lp-btn-primary lp-btn-lg">
              Get Your Custom App
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 7h8m-3-3l3 3-3 3" /></svg>
            </a>
          </div>
        </div>
      </section>

      {/* 03 MULTI-LANGUAGE */}
      <section className="hv-block" style={{ background: "var(--bg-deep)" }}>
        <div className="lp-container">
          <div className="hv-block-head">
            <span className="lp-eyebrow">
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="6" cy="6" r="4.5" /><path d="M1.5 6h9M6 1.5C7.5 3 8 4.5 8 6s-.5 3-2 4.5M6 1.5C4.5 3 4 4.5 4 6s.5 3 2 4.5" /></svg>
              Global Reach
            </span>
            <h2>Multi-language support: <span className="lp-accent">coach clients worldwide</span></h2>
            <p>Break language barriers and expand your coaching business globally. Our apps support multiple languages, allowing you to serve clients from different countries and cultures with localized experiences.</p>
          </div>
          <div className="hv-lang-grid">
            {languages.map((l) => (
              <div key={l.name} className="hv-lang">
                <div
                  className="hv-flag"
                  style={l.isPlus ? { background: "var(--accent-soft)", color: "var(--accent)", fontFamily: "var(--display)", fontWeight: 700, fontSize: 16 } : undefined}
                >
                  {l.flag}
                </div>
                <div>
                  <div className="hv-name">{l.name}</div>
                  <div className="hv-region">{l.region}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="hv-lang-stats">
            <div className="hv-lang-stat"><div className="hv-v">7<span style={{ fontSize: "60%" }}>+</span></div><div className="hv-l">Supported languages</div></div>
            <div className="hv-lang-stat"><div className="hv-v">50<span style={{ fontSize: "60%" }}>+</span></div><div className="hv-l">Countries reached</div></div>
            <div className="hv-lang-stat"><div className="hv-v">100<span style={{ fontSize: "60%" }}>%</span></div><div className="hv-l">Localized experience</div></div>
          </div>
        </div>
      </section>

      {/* 04 WHITE-LABELING */}
      <section className="hv-block">
        <div className="lp-container">
          <div className="hv-wl">
            <div>
              <div className="hv-num">04 · YOUR BRAND, YOUR APP</div>
              <h2>
                True white-labeling: we don't just change icons,<br />
                we <span className="lp-accent">create brands</span>.
              </h2>
              <p className="hv-wl-lead">
                While competitors simply swap app icons and call it "branded," we create completely custom
                experiences from the ground up. Every screen, interaction, and detail reflects your unique
                brand identity — available in both App Store and Google Play under your name.
              </p>
              <ul className="hv-wl-list">
                <Check>Complete custom UI/UX design (not just icon swaps)</Check>
                <Check>Published under YOUR business name in app stores</Check>
                <Check>Fully branded experience from splash screen to checkout</Check>
                <Check>Custom color schemes, fonts, and brand guidelines integration</Check>
                <Check>Dedicated hosting and white-label technical support</Check>
              </ul>
            </div>

            <div className="hv-wl-pair">
              <div className="hv-wl-phone hv-wl-phone-1">
                <div className="hv-wl-phone-screen">
                  <img src="/images/white-label-1.webp" alt="White-label coaching app example 1" loading="lazy" />
                </div>
              </div>
              <div className="hv-wl-phone hv-wl-phone-2">
                <div className="hv-wl-phone-screen">
                  <img src="/images/white-label-2.webp" alt="White-label coaching app example 2" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 05 DISCOVERY */}
      <section id="discovery" className="hv-block" style={{ background: "var(--bg-deep)" }}>
        <div className="lp-container">
          <div className="hv-block-head" style={{ textAlign: "left", maxWidth: "none", marginBottom: 48 }}>
            <span className="lp-eyebrow">05 · GET IN TOUCH</span>
            <h2 style={{ maxWidth: 760 }}>Start your <span className="lp-accent">LVLUP journey</span></h2>
            <p style={{ maxWidth: 580 }}>Ready to transform your coaching business and deliver exceptional results to your clients? Book a free discovery call to learn how LVLUP can help you scale your coaching practice.</p>
          </div>

          <div className="hv-journey">
            <div>
              <div className="hv-step">
                <div className="hv-step-num">1</div>
                <div>
                  <h4>Book a discovery call</h4>
                  <p>Schedule a free 30-minute call to discuss your coaching goals and see how LVLUP fits your needs.</p>
                </div>
              </div>
              <div className="hv-step">
                <div className="hv-step-num">2</div>
                <div>
                  <h4>Customize your setup</h4>
                  <p>We'll help you configure LVLUP to match your coaching style and business requirements.</p>
                </div>
              </div>
              <div className="hv-step">
                <div className="hv-step-num">3</div>
                <div>
                  <h4>Launch &amp; scale</h4>
                  <p>Start delivering premium coaching experiences and grow your business with confidence.</p>
                </div>
              </div>
            </div>

            <div className="hv-booking">
              <h3 className="hv-booking-title">Schedule your free call</h3>
              {calendarReady && (
                <iframe
                  src={BOOKING_IFRAME_SRC}
                  title="Book Discovery Call"
                  id="hv-booking-widget"
                  scrolling="no"
                  className="hv-booking-iframe"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="lp-cta-banner">
        <div className="lp-cta-card">
          <div>
            <h2 className="lp-h2 lp-cta-h2">Ready to go<br />High Value?</h2>
            <p className="lp-cta-p">Talk to our team. We'll map your workflow, scope your app, and quote accurately — so you only pay for what you actually need.</p>
          </div>
          <a href={DISCOVERY_ANCHOR} className="lp-btn lp-btn-primary lp-btn-lg">
            Book Discovery Call
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 7h8m-3-3l3 3-3 3" /></svg>
          </a>
        </div>
      </div>

      </main>

      <SiteFooter />
    </div>
  );
}
